/**
 * Entry point - bootstraps React + i18n + router.
 *
 * The order matters: i18n must initialise before any component renders
 * (because t() reads the active language at call time, and we want
 * the English catalogue ready). React StrictMode wraps the whole tree
 * because strict-from-day-0 catches the common act() + effect cleanup
 * mistakes before they ship.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

// App.tsx lives at the frontend root - peer of main.tsx, NOT under
// shared/. It's the composition root where the three modules are
// wired together, which is the one place that's allowed to import
// from foundation/, twin/, AND framework/. See App.tsx docstring +
// docs/module-boundaries.md.
import { App } from "./App";
import { queryClient } from "shared/api/queryClient";
import { AuthProvider } from "shared/auth/AuthProvider";
import "shared/i18n";
import "./index.css";

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error(
    "MARSAD bootstrap: #root element not found in index.html. " +
      "Did the HTML template get mangled?",
  );
}

// P392: we ARE on React Router v7 now, and the early bet paid off.
//
// This used to pass `future={{v7_startTransition, v7_relativeSplatPath}}`
// with a note saying to opt in on day 0 so that "the v7 upgrade is a
// version bump, not a behavioural one". It was, exactly: the whole 6->7
// migration across 163 importing files was deleting that one prop (the
// flags are defaults in v7, so the prop no longer exists) and nothing
// else. No API changed, no route behaved differently.
//
// Recorded because the payoff is invisible otherwise -- a migration that
// costs nothing looks like it was never going to cost anything.

// Provider order matters:
//   AuthProvider (outermost) - so it can wire the api-client token
//     getter BEFORE QueryClientProvider's first request fires.
//   QueryClientProvider - inside Auth so hooks read the live token.
//   BrowserRouter - inside QueryClient so route components can use
//     query hooks.
createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
